import React, { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Plus,
  Trash2,
  MessageSquare,
  GripVertical,
  ChevronDown,
  AlertTriangle,
} from "lucide-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import type { DragEndEvent } from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useForm } from "react-hook-form";
import {
  ConsultationQuestion,
  ConsultationSection,
  ConsultationTemplate,
  TemplateSectionErrors,
  useConsultationsData,
  validateTemplateSections,
} from "@/lib/settings/ConsultationsData";
import { useToast } from "@/hooks/use-toast";

// ─── Types ────────────────────────────────────────────────────────────────────

interface EditTemplateModalProps {
  /** Accepts both the API/patient shape and the settings shape */
  template: any;
  onClose: () => void;
  onDelete?: () => void;
  templateNameToShow: any;
}

interface ConfirmState {
  open: boolean;
  message: string;
  onConfirm: () => void;
}

// ─── Normalizer ───────────────────────────────────────────────────────────────
// Maps the API / patient-state template shape to the ConsultationTemplate shape.
//
// API shape          →  Form shape
// ─────────────────────────────────
// _id                →  id
// templateName       →  name
// templateSections   →  sections
//   sectionTitle     →    title
//   groupId          →    id  (fallback: generated)
//   userControls     →    questions
//     label          →      question
//     (no id)        →      id  (generated)
// ─────────────────────────────────────────────────────────────────────────────

function normalizeTemplate(raw: any): ConsultationTemplate {
  if (!raw) {
    return {
      id: "",
      name: "",
      description: "",
      category: "general",
      companyID: "",
      consultationSummary: "",
      isDefault: false,
      isActive: true,
      sections: [],
      createdAt: "",
      createdBy: "",
      createdByName: "",
      updatedAt: "",
      updatedBy: "",
      updatedByName: "",
      archiveAt: "",
      archiveBy: "",
      archiveByName: "",
      usageCount: 0,
      freeTextContent: "",
      tempPrompt: "",
      linkedDocumentId: "",
      forceUserSignOff: false,
      lastReviewedAt: "",
      reviewNote: "",
    };
  }

  // Detect which shape we have
  const isNewFormat = Array.isArray(raw?.sections);

  // Stable ID helper — uses the existing id/groupId if present,
  // otherwise derives a deterministic id from title + index so that
  // re-opening the same template always produces the same ids.
  const stableSectionId = (s: any, si: number): string => {
    if (s.id && s.id !== "null") return s.id;
    if (s.groupId && s.groupId !== "null") return s.groupId;
    const title = (s.sectionTitle || s.title || "")
      .replace(/\s+/g, "_")
      .toLowerCase();
    return title ? `section-${title}-${si}` : `section-${si}`;
  };

  const stableQuestionId = (
    q: any,
    sectionTitle: string,
    si: number,
    qi: number,
  ): string => {
    if (q.id && q.id !== "null") return q.id;
    const base = sectionTitle.replace(/\s+/g, "_").toLowerCase();
    return base ? `${base}-${qi}` : `q-${si}-${qi}`;
  };

  const sections: ConsultationSection[] = isNewFormat
    ? (raw.sections || []).map((s: any, si: number) => {
        const id = stableSectionId(s, si);
        return {
          id,
          title: s.title || "",
          questions: (s.questions || []).map((q: any, qi: number) => ({
            id: stableQuestionId(q, s.title || "", si, qi),
            question: q.question || "",
            required: !!q.required,
            value: q.value ?? "",
          })),
        };
      })
    : (raw?.templateSections || []).map((s: any, si: number) => {
        const id = stableSectionId(s, si);
        return {
          id,
          title: s.sectionTitle || "",
          questions: (s.userControls || []).map((c: any, ci: number) => ({
            id: stableQuestionId(c, s.sectionTitle || "", si, ci),
            question: c.label || "",
            required: !!c.required,
            value: c.value ?? "",
          })),
        };
      });

  return {
    id: raw?.id || raw?._id || "",
    name: raw?.name || raw?.templateName || "",
    description: raw?.description || "",
    category: raw?.category || "general",
    companyID: raw?.companyID || "",
    consultationSummary: raw?.consultationSummary || "",
    isDefault: !!raw?.isDefault,
    // Preserve isActive — default true only when truly absent
    isActive:
      raw?.isActive !== undefined && raw?.isActive !== null
        ? raw.isActive
        : true,
    sections,
    createdAt: raw?.createdOn || raw?.createdAt || "",
    createdBy: raw?.createdBy || "",
    createdByName: raw?.createdByName || "",
    updatedAt: raw?.updatedOn || raw?.updatedAt || "",
    updatedBy: raw?.updatedBy || "",
    updatedByName: raw?.updatedByName || "",
    archiveAt: raw?.archiveOn || raw?.archiveAt || "",
    archiveBy: raw?.archiveBy || "",
    archiveByName: raw?.archiveByName || "",
    // Use ?? so usageCount: 0 is preserved (not overwritten by || 0)
    usageCount: raw?.usageCount ?? 0,
    freeTextContent: raw?.freeTextContent || "",
    tempPrompt: raw?.tempPrompt || "",
    linkedDocumentId: raw?.linkedDocumentId || "",
    forceUserSignOff: !!raw?.forceUserSignOff,
    // Normalize null/undefined → "" so the API always gets a string
    lastReviewedAt: raw?.lastReviewedAt ?? "",
    reviewNote: raw?.reviewNote ?? "",
  };
}

// ─── Inline Confirm Dialog ────────────────────────────────────────────────────

function InlineConfirmDialog({
  confirmState,
  onCancel,
  isDarkMode,
}: {
  confirmState: ConfirmState;
  onCancel: () => void;
  isDarkMode: boolean;
}) {
  if (!confirmState.open) return null;
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onCancel} />
      <div
        className={`relative z-10 rounded-lg shadow-xl p-6 w-full max-w-sm mx-4 transition-colors duration-300 ${
          isDarkMode ? "bg-gray-800 text-white" : "bg-white text-gray-900"
        }`}
      >
        <div className="flex items-center gap-3 mb-4">
          <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <p className="text-sm font-medium">{confirmState.message}</p>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            size="sm"
            className="bg-red-600 hover:bg-red-700 text-white"
            onClick={() => {
              confirmState.onConfirm();
              onCancel();
            }}
          >
            Delete
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Sortable Question Item ───────────────────────────────────────────────────

interface SortableQuestionItemProps {
  question: ConsultationQuestion;
  questionIndex: number;
  sectionIndex: number;
  isDarkMode: boolean;
  fieldError?: string;
  updateQuestion: (
    si: number,
    qi: number,
    field: keyof ConsultationQuestion,
    value: any,
  ) => void;
  onDeleteQuestion: (si: number, qi: number) => void;
}

function SortableQuestionItem({
  question,
  questionIndex,
  sectionIndex,
  isDarkMode,
  fieldError,
  updateQuestion,
  onDeleteQuestion,
}: SortableQuestionItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: question.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`rounded-lg p-3 transition-colors duration-300 ${
        isDarkMode
          ? "bg-gray-700 hover:bg-gray-600"
          : "bg-gray-100 hover:bg-gray-200"
      }`}
    >
      <div className="flex items-start gap-3">
        <button
          {...attributes}
          {...listeners}
          type="button"
          className={`cursor-grab active:cursor-grabbing flex-shrink-0 mt-1 ${
            isDarkMode
              ? "text-gray-400 hover:text-gray-300"
              : "text-gray-500 hover:text-gray-700"
          }`}
        >
          <GripVertical className="w-4 h-4" />
        </button>

        <div className="flex-1 space-y-3">
          <Textarea
            value={question.question || ""}
            onChange={(e) =>
              updateQuestion(
                sectionIndex,
                questionIndex,
                "question",
                e.target.value,
              )
            }
            placeholder="Enter your question..."
            rows={2}
            className={`text-sm ${
              isDarkMode
                ? "bg-gray-800 border-gray-600 text-white"
                : "bg-white border-gray-300"
            } ${fieldError ? "border-red-500" : ""}`}
          />
          {fieldError && <p className="text-sm text-red-500">{fieldError}</p>}

          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Switch
                checked={!!question.required}
                onCheckedChange={(checked) =>
                  updateQuestion(
                    sectionIndex,
                    questionIndex,
                    "required",
                    checked,
                  )
                }
              />
              <span
                className={`text-sm ${
                  isDarkMode ? "text-gray-300" : "text-gray-600"
                }`}
              >
                Required
              </span>
            </div>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => onDeleteQuestion(sectionIndex, questionIndex)}
              className="h-auto p-1 text-red-500 hover:text-red-700"
            >
              <Trash2 className="w-3 h-3" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Sortable Section Item ────────────────────────────────────────────────────

interface SortableSectionItemProps {
  section: ConsultationSection;
  sectionIndex: number;
  isExpanded: boolean;
  isDarkMode: boolean;
  fieldErrors: TemplateSectionErrors;
  updateSection: (
    index: number,
    field: keyof ConsultationSection,
    value: any,
  ) => void;
  onDeleteSection: (index: number) => void;
  toggleSection: (id: string) => void;
  addQuestion: (index: number) => void;
  updateQuestion: (
    si: number,
    qi: number,
    field: keyof ConsultationQuestion,
    value: any,
  ) => void;
  onDeleteQuestion: (si: number, qi: number) => void;
  reorderQuestions: (si: number, startIndex: number, endIndex: number) => void;
}

function SortableSectionItem({
  section,
  sectionIndex,
  isExpanded,
  isDarkMode,
  fieldErrors,
  updateSection,
  onDeleteSection,
  toggleSection,
  addQuestion,
  updateQuestion,
  onDeleteQuestion,
  reorderQuestions,
}: SortableSectionItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const questionSensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Guard: ensure questions is always an array
  const questions = Array.isArray(section.questions) ? section.questions : [];

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`border rounded-lg overflow-hidden transition-colors duration-300 ${
        isDarkMode ? "border-gray-600" : "border-gray-200"
      }`}
    >
      {/* Section Header */}
      <div
        className={`flex items-center justify-between p-4 transition-colors duration-300 ${
          isDarkMode ? "bg-gray-800" : "bg-white"
        }`}
      >
        <div className="flex items-center flex-1 gap-3">
          <button
            {...attributes}
            {...listeners}
            type="button"
            className={`cursor-grab active:cursor-grabbing flex-shrink-0 ${
              isDarkMode
                ? "text-gray-400 hover:text-gray-300"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <GripVertical className="w-5 h-5" />
          </button>

          <div
            className={`w-6 h-6 rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0 ${
              isDarkMode ? "bg-gray-600 text-white" : "bg-black text-white"
            }`}
          >
            {sectionIndex + 1}
          </div>

          <Input
            value={section.title || ""}
            onChange={(e) =>
              updateSection(sectionIndex, "title", e.target.value)
            }
            className={`font-medium text-base bg-transparent border-0 px-0 py-0 h-auto focus:ring-0 ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
            placeholder="Section name..."
          />
        </div>

        <div className="flex items-center gap-2">
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => addQuestion(sectionIndex)}
            className={`p-1 h-auto ${
              isDarkMode
                ? "text-gray-400 hover:text-white"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <Plus className="w-4 h-4" />
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => onDeleteSection(sectionIndex)}
            className="h-auto p-1 text-red-500 hover:text-red-700"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => toggleSection(section.id)}
            className={`p-1 h-auto ${
              isDarkMode
                ? "text-gray-400 hover:text-white"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <ChevronDown
              className={`w-4 h-4 transition-all duration-300 ${
                isExpanded ? "rotate-180" : ""
              }`}
            />
          </Button>
        </div>
      </div>
      {(fieldErrors[`title-${sectionIndex}`] ||
        fieldErrors[`questions-${sectionIndex}`]) && (
        <div
          className={`px-4 pb-3 space-y-1 ${
            isDarkMode ? "bg-gray-800" : "bg-white"
          }`}
        >
          {fieldErrors[`title-${sectionIndex}`] && (
            <p className="text-sm text-red-500">
              {fieldErrors[`title-${sectionIndex}`]}
            </p>
          )}
          {fieldErrors[`questions-${sectionIndex}`] && (
            <p className="text-sm text-red-500">
              {fieldErrors[`questions-${sectionIndex}`]}
            </p>
          )}
        </div>
      )}

      {/* Questions — only when expanded */}
      {isExpanded && (
        <div
          className={`border-t p-4 space-y-3 transition-colors duration-300 ${
            isDarkMode
              ? "border-gray-600 bg-gray-900"
              : "border-gray-200 bg-gray-50"
          }`}
        >
          <DndContext
            sensors={questionSensors}
            collisionDetection={closestCenter}
            onDragEnd={(event: DragEndEvent) => {
              const { active, over } = event;
              if (over && active.id !== over.id) {
                const oldIndex = questions.findIndex((q) => q.id === active.id);
                const newIndex = questions.findIndex((q) => q.id === over.id);
                if (oldIndex !== -1 && newIndex !== -1) {
                  reorderQuestions(sectionIndex, oldIndex, newIndex);
                }
              }
            }}
          >
            <SortableContext
              items={questions.map((q) => q.id)}
              strategy={verticalListSortingStrategy}
            >
              {questions.map((question, questionIndex) => (
                <SortableQuestionItem
                  key={question.id}
                  question={question}
                  questionIndex={questionIndex}
                  sectionIndex={sectionIndex}
                  isDarkMode={isDarkMode}
                  fieldError={
                    fieldErrors[`question-${sectionIndex}-${questionIndex}`]
                  }
                  updateQuestion={updateQuestion}
                  onDeleteQuestion={onDeleteQuestion}
                />
              ))}
            </SortableContext>
          </DndContext>

          {questions.length === 0 && (
            <div
              className={`text-center py-4 border-2 border-dashed rounded-lg ${
                isDarkMode ? "border-gray-600" : "border-gray-300"
              }`}
            >
              <p
                className={`text-sm ${
                  isDarkMode ? "text-gray-400" : "text-gray-600"
                }`}
              >
                No questions yet. Click the + button to add a question.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Main Modal ───────────────────────────────────────────────────────────────

export function EditTemplateModal({
  template,
  onClose,
  onDelete,
  templateNameToShow,
}: EditTemplateModalProps) {
  const {
    darkMode: isDarkMode,
    handleTemplateSubmit,
    buttonDisabled,
    hasRequiredHeading,
  } = useConsultationsData();
  const { toast } = useToast();

  // ── Confirm dialog ──
  const [confirmState, setConfirmState] = useState<ConfirmState>({
    open: false,
    message: "",
    onConfirm: () => {},
  });

  const confirmDelete = useCallback(
    (onConfirm: () => void, message?: string) => {
      setConfirmState({
        open: true,
        message: message || "Are you sure you want to delete this item?",
        onConfirm,
      });
    },
    [],
  );

  // ── Initialize normalized form data ──
  const [formData, setFormData] = useState<ConsultationTemplate>(() =>
    normalizeTemplate(template),
  );
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    () =>
      new Set((normalizeTemplate(template).sections || []).map((s) => s.id)),
  );

  const { setValue, reset } = useForm<ConsultationTemplate>({
    defaultValues: normalizeTemplate(template),
  });

  // Inline errors for blank section titles / questions (see validateTemplateSections)
  const [sectionFieldErrors, setSectionFieldErrors] =
    useState<TemplateSectionErrors>({});
  const clearSectionFieldError = (key: string) =>
    setSectionFieldErrors((prev) => {
      if (!(key in prev)) return prev;
      const { [key]: _removed, ...rest } = prev;
      return rest;
    });

  // Re-normalize when a different template is passed in
  useEffect(() => {
    const normalized = normalizeTemplate(template);
    setFormData(normalized);
    reset(normalized);
    setExpandedSections(new Set((normalized.sections || []).map((s) => s.id)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template?._id, template?.id]);

  // ── DnD sensors ──
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // ── Derived safe sections array ──
  const sections = Array.isArray(formData.sections) ? formData.sections : [];

  // ── Field helpers ──
  const updateField = (field: keyof ConsultationTemplate, value: any) =>
    setFormData((prev) => ({ ...prev, [field]: value }));

  // ── Section CRUD ──
  const addSection = () => {
    const newId = `s${Date.now()}`;
    const newSection: ConsultationSection = {
      id: newId,
      title: "",
      questions: [],
    };
    setFormData((prev) => {
      const updated = [...(prev.sections || []), newSection];
      setValue("sections", updated, {
        shouldValidate: true,
        shouldDirty: true,
      });
      return { ...prev, sections: updated };
    });
    setExpandedSections((prev) => new Set([...prev, newId]));
  };

  const updateSection = (
    index: number,
    field: keyof ConsultationSection,
    value: any,
  ) => {
    if (field === "title") clearSectionFieldError(`title-${index}`);
    setFormData((prev) => {
      const updated = (prev.sections || []).map((s, i) =>
        i === index ? { ...s, [field]: value } : s,
      );
      setValue("sections", updated, {
        shouldValidate: true,
        shouldDirty: true,
      });
      return { ...prev, sections: updated };
    });
  };

  const removeSection = (index: number) => {
    const target = (formData.sections || [])[index];
    const updated = (formData.sections || []).filter((_, i) => i !== index);
    // Positions shift, so position-keyed errors no longer apply.
    setSectionFieldErrors({});
    setFormData((prev) => ({ ...prev, sections: updated }));
    setValue("sections", updated, { shouldValidate: true, shouldDirty: true });
    if (target) {
      setExpandedSections((prev) => {
        const next = new Set(prev);
        next.delete(target.id);
        return next;
      });
    }
  };

  const reorderSections = (startIndex: number, endIndex: number) => {
    const reordered = arrayMove(formData.sections || [], startIndex, endIndex);
    setSectionFieldErrors({});
    setFormData((prev) => ({ ...prev, sections: reordered }));
    setValue("sections", reordered, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const toggleSection = (sectionId: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) next.delete(sectionId);
      else next.add(sectionId);
      return next;
    });
  };

  // ── Question CRUD ──
  const addQuestion = (sectionIndex: number) => {
    const newQuestion: ConsultationQuestion = {
      id: `q${Date.now()}`,
      question: "",
      required: false,
      value: "",
    };
    clearSectionFieldError(`questions-${sectionIndex}`);
    const updated = (formData.sections || []).map((s, i) =>
      i === sectionIndex
        ? { ...s, questions: [...(s.questions || []), newQuestion] }
        : s,
    );
    setFormData((prev) => ({ ...prev, sections: updated }));
    setValue(
      `sections.${sectionIndex}.questions`,
      updated[sectionIndex]?.questions || [],
      { shouldValidate: true, shouldDirty: true },
    );
  };

  const updateQuestion = (
    sectionIndex: number,
    questionIndex: number,
    field: keyof ConsultationQuestion,
    value: any,
  ) => {
    if (field === "question") {
      clearSectionFieldError(`question-${sectionIndex}-${questionIndex}`);
    }
    setFormData((prev) => ({
      ...prev,
      sections: (prev.sections || []).map((s, si) =>
        si === sectionIndex
          ? {
              ...s,
              questions: (s.questions || []).map((q, qi) =>
                qi === questionIndex ? { ...q, [field]: value } : q,
              ),
            }
          : s,
      ),
    }));
  };

  const removeQuestion = (sectionIndex: number, questionIndex: number) => {
    setSectionFieldErrors({});
    const updated = (formData.sections || []).map((s, si) =>
      si === sectionIndex
        ? {
            ...s,
            questions: (s.questions || []).filter(
              (_, qi) => qi !== questionIndex,
            ),
          }
        : s,
    );
    setFormData((prev) => ({ ...prev, sections: updated }));
    setValue(
      `sections.${sectionIndex}.questions`,
      updated[sectionIndex]?.questions || [],
      { shouldValidate: true, shouldDirty: true },
    );
  };

  const reorderQuestions = (
    sectionIndex: number,
    startIndex: number,
    endIndex: number,
  ) => {
    setSectionFieldErrors({});
    const updated = (formData.sections || []).map((s, i) =>
      i === sectionIndex
        ? {
            ...s,
            questions: arrayMove(s.questions || [], startIndex, endIndex),
          }
        : s,
    );
    setFormData((prev) => ({ ...prev, sections: updated }));
    setValue(
      `sections.${sectionIndex}.questions`,
      updated[sectionIndex]?.questions || [],
      { shouldValidate: true, shouldDirty: true },
    );
  };

  // ── Save ──
  // Matches the payload shape that ConsultationFormPanel (ConsultationSettings)
  // sends via handleTemplateSubmit — same fields, same structure.
  const onSubmit = async () => {
    const { errors: sectionErrors, sectionIdsWithErrors } =
      validateTemplateSections(formData.sections || []);
    setSectionFieldErrors(sectionErrors);
    if (Object.keys(sectionErrors).length > 0) {
      setExpandedSections(
        (prev) => new Set([...Array.from(prev), ...sectionIdsWithErrors]),
      );
      return;
    }

    const payload: ConsultationTemplate = {
      // Core identity — preserved from original template
      id: formData.id,
      name: formData.name,
      description: formData.description || "",
      category: formData.category || "general",
      companyID: formData.companyID || "",
      // Editable content
      consultationSummary: formData.consultationSummary || "",
      sections: formData.sections || [],
      // Flags
      isDefault: formData.isDefault,
      isActive: formData.isActive,
      linkedDocumentId: formData.linkedDocumentId || "",
      forceUserSignOff: formData.forceUserSignOff || false,
      // Audit fields — pass through unchanged
      createdAt: formData.createdAt || "",
      createdBy: formData.createdBy || "",
      createdByName: formData.createdByName || "",
      updatedAt: formData.updatedAt || "",
      updatedBy: formData.updatedBy || "",
      updatedByName: formData.updatedByName || "",
      archiveAt: formData.archiveAt || "",
      archiveBy: formData.archiveBy || "",
      archiveByName: formData.archiveByName || "",
      usageCount: formData.usageCount ?? 0,
      freeTextContent: formData.freeTextContent || "",
      tempPrompt: formData.tempPrompt || "",
      lastReviewedAt:
        formData.lastReviewedAt === "" ? null : formData.lastReviewedAt,
      reviewNote: formData.reviewNote ?? "",
    };

    await handleTemplateSubmit(payload, () => {
      toast({
        title: "Template Updated",
        description: "Your consultation template has been saved successfully.",
      });
      onClose();
    });
  };

  const hasHeading = hasRequiredHeading(formData.consultationSummary || "");
  const canSave = !buttonDisabled && hasHeading && !!formData.name?.trim();

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4"
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
      >
        {/* Modal */}
        <div
          className={`rounded-xl w-full max-w-6xl h-[85vh] flex flex-col shadow-2xl transition-colors duration-300 ${
            isDarkMode ? "bg-gray-900" : "bg-white"
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* ── Header ── */}
          <div
            className={`flex items-center gap-4 border-b px-6 py-4 rounded-t-xl flex-shrink-0 transition-colors duration-300 ${
              isDarkMode
                ? "bg-gray-900 border-gray-700"
                : "bg-white border-gray-200"
            }`}
          >
            {/* Editable template name */}
            <div className="flex-1 min-w-0">
              <Input
                value={formData.name || ""}
                onChange={(e) => {
                  updateField("name", e.target.value);
                  setValue("name", e.target.value, { shouldValidate: true });
                }}
                className={`w-full font-bold bg-transparent border-0 px-0 py-0 h-auto focus:ring-0 text-xl transition-colors duration-300 ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
                placeholder="Template name..."
              />
              {!formData.name?.trim() && (
                <p className="text-xs text-red-500 mt-0.5">
                  Template name is required
                </p>
              )}
            </div>

            {/* Action buttons */}
            <div className="flex items-center gap-2 flex-shrink-0">
              {formData.consultationSummary && !hasHeading && (
                <p className="text-xs text-red-500 max-w-[200px] text-right">
                  Notes template must contain at least one heading
                </p>
              )}
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                className={
                  isDarkMode
                    ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                }
              >
                Cancel
              </Button>
              {template.templateName !== templateNameToShow && onDelete && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() =>
                    confirmDelete(() => {
                      onDelete();
                      onClose();
                    }, "Are you sure you want to delete this template? This action cannot be undone.")
                  }
                  className="border-red-500 text-red-500 hover:bg-red-50 hover:text-red-600"
                >
                  Delete
                </Button>
              )}
              <Button
                type="button"
                disabled={!canSave}
                onClick={onSubmit}
                className="bg-purple-600 hover:bg-purple-700 text-white disabled:opacity-50"
              >
                {buttonDisabled ? "Saving..." : "Update Template"}
              </Button>
            </div>
          </div>

          {/* ── Two-Panel Body ── */}
          <div className="flex-1 flex overflow-hidden min-h-0">
            {/* Left — Generated Notes Template */}
            <div
              className={`flex-1 flex flex-col border-r min-w-0 transition-colors duration-300 ${
                isDarkMode ? "border-gray-700" : "border-gray-200"
              }`}
            >
              <div
                className={`border-b px-4 py-3 flex-shrink-0 transition-colors duration-300 ${
                  isDarkMode
                    ? "bg-gray-800 border-gray-700"
                    : "bg-gray-50 border-gray-200"
                }`}
              >
                <h2
                  className={`font-medium transition-colors duration-300 ${
                    isDarkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  Generated Notes Template
                </h2>
                <p
                  className={`text-sm transition-colors duration-300 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Edit the consultation notes template as needed
                </p>
              </div>

              <div className="flex-1 min-h-0 p-3 flex flex-col">
                <Textarea
                  value={formData.consultationSummary || ""}
                  onChange={(e) => {
                    const val = e.target.value;
                    updateField("consultationSummary", val);
                    setValue("consultationSummary", val, {
                      shouldValidate: true,
                      shouldDirty: true,
                    });
                  }}
                  className={`flex-1 w-full resize-none font-mono text-sm border-0 focus:ring-0 transition-colors duration-300 h-full ${
                    isDarkMode
                      ? "bg-gray-900 text-gray-200"
                      : "bg-white text-gray-900"
                  }`}
                  placeholder="Consultation notes template..."
                />
                {formData.consultationSummary && !hasHeading && (
                  <p className="text-xs text-red-500 mt-1">
                    At least one heading (e.g. "Medical History") must be
                    present to save.
                  </p>
                )}
              </div>
            </div>

            {/* Right — Structured Questions */}
            <div className="w-1/2 flex flex-col min-w-0">
              <div
                className={`border-b px-4 py-3 flex-shrink-0 transition-colors duration-300 ${
                  isDarkMode
                    ? "bg-gray-800 border-gray-700"
                    : "bg-gray-50 border-gray-200"
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h2
                      className={`font-medium transition-colors duration-300 ${
                        isDarkMode ? "text-white" : "text-gray-900"
                      }`}
                    >
                      Structured Questions
                    </h2>
                    <p
                      className={`text-sm transition-colors duration-300 ${
                        isDarkMode ? "text-gray-400" : "text-gray-600"
                      }`}
                    >
                      Questions grouped by consultation sections
                    </p>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    onClick={addSection}
                    className="text-white bg-purple-600 hover:bg-purple-700"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Section
                  </Button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={(event: DragEndEvent) => {
                    const { active, over } = event;
                    if (over && active.id !== over.id) {
                      const oldIndex = sections.findIndex(
                        (s) => s.id === active.id,
                      );
                      const newIndex = sections.findIndex(
                        (s) => s.id === over.id,
                      );
                      if (oldIndex !== -1 && newIndex !== -1) {
                        reorderSections(oldIndex, newIndex);
                      }
                    }
                  }}
                >
                  <SortableContext
                    items={sections.map((s) => s.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    {sections.map((section, sectionIndex) => (
                      <SortableSectionItem
                        key={section.id}
                        section={section}
                        sectionIndex={sectionIndex}
                        isExpanded={expandedSections.has(section.id)}
                        isDarkMode={isDarkMode}
                        fieldErrors={sectionFieldErrors}
                        updateSection={updateSection}
                        onDeleteSection={(index) =>
                          confirmDelete(
                            () => removeSection(index),
                            "Are you sure you want to delete this section and all its questions?",
                          )
                        }
                        toggleSection={toggleSection}
                        addQuestion={addQuestion}
                        updateQuestion={updateQuestion}
                        onDeleteQuestion={(si, qi) =>
                          confirmDelete(
                            () => removeQuestion(si, qi),
                            "Are you sure you want to delete this question?",
                          )
                        }
                        reorderQuestions={reorderQuestions}
                      />
                    ))}
                  </SortableContext>
                </DndContext>

                {sections.length === 0 && (
                  <div
                    className={`text-center py-10 border-2 border-dashed rounded-lg transition-colors duration-300 ${
                      isDarkMode ? "border-gray-600" : "border-gray-300"
                    }`}
                  >
                    <MessageSquare
                      className={`w-8 h-8 mx-auto mb-2 transition-colors duration-300 ${
                        isDarkMode ? "text-gray-600" : "text-gray-400"
                      }`}
                    />
                    <p
                      className={`text-sm transition-colors duration-300 ${
                        isDarkMode ? "text-gray-400" : "text-gray-600"
                      }`}
                    >
                      No questions added yet. Click "Add Section" to get
                      started.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Inline confirm dialog */}
      <InlineConfirmDialog
        confirmState={confirmState}
        onCancel={() =>
          setConfirmState({ open: false, message: "", onConfirm: () => {} })
        }
        isDarkMode={isDarkMode}
      />
    </>
  );
}

export default EditTemplateModal;
